/** 项目模板与文案配置；动态清单沿现有生成器契约，保存实际输入变更统一失效 HTML。 */
import { type LandingCopy, LandingCopySchema } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  type ProjectDetail,
  type ProjectMutation,
  useLandingProjectDefaultCopy,
  useLandingTemplates,
} from '~/data/queries';
import { landingProjectsApi } from '~/hooks/use-landing-projects';
import { Icons } from '~/lib/icons';
import { FormRow } from '~/ui/FormRow';
import { Select } from '~/ui/Select';
import { Spinner } from '~/ui/Skeleton';
import { Switch } from '~/ui/Switch';
import { toast } from '~/ui/Toast';
const hasCopyText = (copy: LandingCopy | null | undefined) =>
  !!copy && Object.values(copy).some((value) => value.trim().length > 0);
const emptyCopy: LandingCopy = {
  cta: '',
  hook_line1: '',
  hook_line2: '',
  topbar_tagline: '',
  topbar_cta: '',
};
export function LandingProjectTemplatePanel({
  project,
  onChanged,
}: { project: ProjectDetail; onChanged: (result: ProjectMutation) => void }) {
  const templates = useLandingTemplates();
  const client = useQueryClient();
  const defaultCopyQuery = useLandingProjectDefaultCopy(project.product.id, project.novel.locale);
  const systemCopyResult = LandingCopySchema.safeParse(
    templates.data?.copy?.defaults?.[project.novel.locale],
  );
  const systemDefaults =
    systemCopyResult.success && hasCopyText(systemCopyResult.data)
      ? systemCopyResult.data
      : undefined;
  const productDefaults = defaultCopyQuery.data?.copy;
  const defaults = defaultCopyQuery.isSuccess
    ? hasCopyText(productDefaults)
      ? productDefaults
      : systemDefaults
    : undefined;
  // 按字段值同步服务端配置，避免后台刷新返回新对象时清空正在编辑的草稿。
  const storedCopy = JSON.stringify({
    projectId: project.id,
    copy: { ...emptyCopy, ...project.copy_overrides },
  });
  const [template, setTemplate] = useState(project.template_id ?? '');
  const [topbar, setTopbar] = useState(project.show_topbar);
  const [copy, setCopy] = useState<LandingCopy>({ ...emptyCopy, ...project.copy_overrides });
  const [customCopy, setCustomCopy] = useState(hasCopyText(project.copy_overrides));
  useEffect(() => {
    setTemplate(project.template_id ?? '');
    setTopbar(project.show_topbar);
    const { copy: savedCopy }: { copy: LandingCopy } = JSON.parse(storedCopy);
    setCopy(savedCopy);
    setCustomCopy(hasCopyText(savedCopy));
  }, [project.template_id, project.show_topbar, storedCopy]);
  const save = useMutation({
    mutationFn: () => {
      const resolved = customCopy ? copy : defaults;
      const parsed = LandingCopySchema.safeParse(resolved);
      if (!parsed.success) throw new Error('对应语言的完整文案未就绪，请重试加载或填写自定义文案');
      return landingProjectsApi.update(project.id, {
        template_id: template || null,
        show_topbar: topbar,
        copy_overrides: parsed.data,
      });
    },
    onSuccess: onChanged,
    onError: (error: Error) => toast(error.message, { tone: 'neg' }),
  });
  if (templates.isLoading)
    return (
      <div className="landing-project-state" role="status">
        <Spinner />
        <span>加载模板与文案清单…</span>
      </div>
    );
  if (templates.isError || !templates.data)
    return (
      <div className="card landing-project-state" role="alert">
        <Icons.IconAlert size={18} style={{ color: 'var(--neg)' }} />
        <span className="landing-project-state-message">模板清单加载失败，无法保存配置。</span>
        <button
          type="button"
          className="btn"
          onClick={() => void client.refetchQueries({ queryKey: ['landing-templates'] })}
        >
          重新加载
        </button>
      </div>
    );
  return (
    <div className="card" style={{ padding: '0 18px 18px' }}>
      <FormRow label="HTML 模板" required>
        <Select value={template} onChange={(event) => setTemplate(event.target.value)}>
          <option value="">选择模板…</option>
          {templates.data.templates.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name ?? item.id}
              {item.needs_image ? ' · 需要投放素材' : ' · 纯文字'}
            </option>
          ))}
        </Select>
        <p className="muted">更改模板影响当前项目所有任务；新 HTML 生成后需要重新下载。</p>
      </FormRow>
      {templates.data.options?.some((option) => option.id === 'show_topbar') && (
        <FormRow label="顶部下载条">
          <Switch ariaLabel="顶部下载条" checked={topbar} onChange={setTopbar} />
        </FormRow>
      )}

      <FormRow label="自定义页面文案" hint={`严格使用 ${project.novel.locale} 语言文案`}>
        <Switch
          ariaLabel="自定义页面文案"
          checked={customCopy}
          onChange={(enabled) => {
            if (enabled && !hasCopyText(copy) && defaults) setCopy({ ...defaults });
            setCustomCopy(enabled);
          }}
        />
        {!customCopy && (
          <p className="muted">
            使用产品已配置文案；产品文案全空时使用系统同语言默认值。保存后冻结到当前项目。
          </p>
        )}
      </FormRow>
      {defaultCopyQuery.isPending && <p>加载对应语言默认文案…</p>}
      {defaultCopyQuery.isError && (
        <p role="alert">
          对应语言文案读取失败：{defaultCopyQuery.error.message}
          <button type="button" className="btn" onClick={() => void defaultCopyQuery.refetch()}>
            重新加载
          </button>
        </p>
      )}
      {/* 产品文案优先、系统默认兜底；两者都缺时才是真阻塞，只缺系统默认不必告警。 */}
      {defaultCopyQuery.isSuccess && !defaults && (
        <p className="muted" role="alert">
          {project.novel.locale}{' '}
          暂无产品文案或完整系统默认文案；请开启自定义文案并填写，或在投放设置中配置该语言默认值。
        </p>
      )}
      {templates.data.copy?.keys?.map((key) => (
        <FormRow
          key={key.key}
          label={key.name ?? key.key}
          hint={key.scope === 'topbar' ? '仅开启顶部下载条时显示' : undefined}
        >
          <input
            className="input"
            aria-label={key.name ?? key.key}
            disabled={!customCopy}
            value={customCopy ? copy[key.key] : (defaults?.[key.key] ?? '')}
            onChange={(event) =>
              setCopy((current) => ({ ...current, [key.key]: event.target.value }))
            }
            maxLength={500}
          />
        </FormRow>
      ))}
      <div className="hstack" style={{ gap: 8, marginTop: 14 }}>
        <button
          type="button"
          className="btn"
          disabled={!systemDefaults}
          title={systemDefaults ? undefined : `未提供 ${project.novel.locale} 的完整系统默认文案`}
          onClick={() => {
            if (!systemDefaults) return;
            setCopy({ ...systemDefaults });
            setCustomCopy(true);
          }}
        >
          填入对应语言默认文案
        </button>
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn btn-accent"
          disabled={!template || save.isPending || (!customCopy && !defaults)}
          onClick={() => save.mutate()}
        >
          {save.isPending ? '保存中…' : '保存配置'}
        </button>
      </div>
    </div>
  );
}
